import { motion } from "framer-motion";
import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CONTACT } from "@/constants/testIds";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    business_name: "",
    email: "",
    topic: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [errors, setErrors] = useState({});

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) {
      e.email = "Please enter your email.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      e.email = "That email doesn't look right.";
    }
    if (!form.topic) e.topic = "Please choose a topic.";
    if (!form.message.trim()) e.message = "Please add a short message.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        business_name: form.business_name.trim() || null,
        email: form.email.trim(),
        topic: form.topic,
        message: form.message.trim(),
      });
      setStatus("done");
      toast.success("Message sent — we'll be in touch shortly.");
      setForm({ name: "", business_name: "", email: "", topic: "", message: "" });
    } catch (err) {
      setStatus("error");
      toast.error("Couldn't send just now — please try again.");
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.root}
      className="bg-cream py-24 md:py-32 relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -left-10 bottom-0 font-serif text-[22vw] leading-none text-terracotta/10 select-none pointer-events-none"
      >
        hello.
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
        <div className="col-span-12 md:col-span-5">
          <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">
            Contact
          </div>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] text-forest tracking-tight">
            How can we <span className="italic font-light">help</span>?
          </h2>
          <p className="mt-6 text-base md:text-lg text-charcoal/75 max-w-md">
            Reach out to us with any questions, partnership, and investment
            opportunities. We reply personally, within 48 hours.
          </p>

          <div className="mt-10 space-y-4 text-forest">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-charcoal/60">
                Email
              </div>
              <a
                href="mailto:support@waylio.com"
                className="link-underline font-serif text-xl md:text-2xl"
              >
                support@waylio.com
              </a>
            </div>
          </div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          onSubmit={onSubmit}
          noValidate
          className="col-span-12 md:col-span-7 md:col-start-6 bg-paper border border-forest/15 p-6 md:p-10 relative"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <Field
              label="Name*"
              testId={CONTACT.formName}
              value={form.name}
              onChange={(v) => setField("name", v)}
              error={errors.name}
            />
            <Field
              label="Business Name"
              testId={CONTACT.formBusiness}
              value={form.business_name}
              onChange={(v) => setField("business_name", v)}
            />
            <Field
              label="Email*"
              type="email"
              testId={CONTACT.formEmail}
              value={form.email}
              onChange={(v) => setField("email", v)}
              error={errors.email}
            />

            <div>
              <label className="block text-[10px] uppercase tracking-[0.3em] text-charcoal/60 mb-2">
                Topic*
              </label>
              <Select value={form.topic} onValueChange={(v) => setField("topic", v)}>
                <SelectTrigger
                  data-testid={CONTACT.formTopic}
                  className="w-full bg-transparent border-0 border-b border-forest/30 rounded-none px-0 h-10 focus:ring-0 focus-visible:ring-0 focus:border-terracotta text-forest text-base"
                >
                  <SelectValue placeholder="Choose a topic" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Inquiry">Inquiry</SelectItem>
                  <SelectItem value="Partnerships">Partnerships</SelectItem>
                  <SelectItem value="Investments">Investments</SelectItem>
                </SelectContent>
              </Select>
              {errors.topic && (
                <div className="mt-1 text-xs text-terracotta">{errors.topic}</div>
              )}
            </div>
          </div>

          <div className="mt-6 md:mt-8">
            <label className="block text-[10px] uppercase tracking-[0.3em] text-charcoal/60 mb-2">
              Message*
            </label>
            <textarea
              data-testid={CONTACT.formMessage}
              rows={5}
              value={form.message}
              onChange={(e) => setField("message", e.target.value)}
              className="w-full bg-transparent border-0 border-b border-forest/30 focus:border-terracotta outline-none py-2 text-forest resize-none"
              placeholder="Tell us how we can help."
            />
            {errors.message && (
              <div className="mt-1 text-xs text-terracotta">{errors.message}</div>
            )}
          </div>

          <div className="mt-8 flex items-center justify-between gap-4">
            <p className="text-[11px] text-charcoal/50 max-w-sm">
              By submitting, you agree to be contacted about your inquiry. We
              don't share your info.
            </p>
            <button
              type="submit"
              data-testid={CONTACT.formSubmit}
              disabled={status === "loading"}
              className="group inline-flex items-center gap-2 bg-forest hover:bg-terracotta text-cream px-6 py-3.5 text-sm font-medium transition-colors disabled:opacity-60"
            >
              {status === "loading" ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending
                </>
              ) : (
                <>
                  Send message
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </>
              )}
            </button>
          </div>

          {status === "done" && (
            <div
              data-testid={CONTACT.success}
              className="mt-6 text-sm text-forest border border-forest/20 bg-cream px-4 py-3"
            >
              Thanks — your message is in. We'll reply within 48 hours.
            </div>
          )}
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, testId, type = "text", error }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.3em] text-charcoal/60 mb-2">
        {label}
      </label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-0 border-b border-forest/30 focus:border-terracotta outline-none py-2 text-forest text-base"
      />
      {error && <div className="mt-1 text-xs text-terracotta">{error}</div>}
    </div>
  );
}
